import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import React from "react";
import ColumnsTable from "./ColumnsTable";
import AddColumnsModal from "../../components/MergeField/AddColumnsModal/AddColumnsModal";

interface Props {
  columns: server.dto.ExcelTemplateColumn[]
  availableMergeFieldTopics: server.dto.AvailableMergeFieldTopic[]
  onColumnsUpdated: (columns: server.dto.ExcelTemplateColumn[]) => void
}

interface DetailProps extends Props {
  excelTemplateId: number
}

export const ColumnsPanelForDetail = (props: DetailProps) => {
  const [isAddColumnsModalOpen, setIsAddColumnsModalOpen] = React.useState(false);

  const onDelete = async (column: server.dto.ExcelTemplateColumn) => {
    const filtered = props.columns.filter(c => c.mergeField !== column.mergeField);
    props.onColumnsUpdated(filtered);
    const instruction: server.dto.AddRemoveExcelTemplateColumnsInstruction = {
      excelTemplateId: props.excelTemplateId,
      removeMergeFields: [column.mergeField],
      addMergeFields: []
    };

    await window.CoreApi.ExcelTemplate.AddRemoveColumns(instruction);
  }

  const onSave = async (column: server.dto.ExcelTemplateColumn) => {
    const updated = props.columns.map(c => c.mergeField === column.mergeField ? column : c);
    props.onColumnsUpdated(updated);
    const instruction: server.dto.UpdateExcelColumnInstruction = {
      excelTemplateId: props.excelTemplateId,
      mergeField: column.mergeField,
      headerLabel: column.headerLabel,
      hexColor: column.hexColor
    };

    await window.CoreApi.ExcelTemplate.UpdateColumn(instruction);
  }

  const onOrderChange = async (newColumns: server.dto.ExcelTemplateColumn[]) => {
    props.onColumnsUpdated(newColumns);
    const instruction: server.dto.UpdateExcelTemplateColumnOrderInstruction = {
      excelTemplateId: props.excelTemplateId,
      mergefields: newColumns.map(c => c.mergeField)
    }

    await window.CoreApi.ExcelTemplate.UpdateColumnOrder(instruction);
  }

  const onAddModalClose = async (mergeFields: string[]) => {
    setIsAddColumnsModalOpen(false);

    const { columnsToAdd, columnsToRemove } = getColumnsToAddAndColumnsToRemove(props.columns, mergeFields);
    const updatedColumns = props.columns.filter(c => !columnsToRemove.some(r => r.mergeField === c.mergeField)).concat(columnsToAdd);

    const instruction: server.dto.AddRemoveExcelTemplateColumnsInstruction = {
      excelTemplateId: props.excelTemplateId,
      addMergeFields: columnsToAdd.map(c => c.mergeField),
      removeMergeFields: columnsToRemove.map(c => c.mergeField)
    }

    await window.CoreApi.ExcelTemplate.AddRemoveColumns(instruction);

    props.onColumnsUpdated(updatedColumns);
  }

  return <>
    <SectionPanel.Panel
      header={<>
        <PanelSegment.Title title={RESX.Template.Columns} />
        <PanelSegment.Button onClick={() => {
          setIsAddColumnsModalOpen(true);
        }} label={RESX.Buttons.Add} type="secondary" />
      </>}
    >
      <ColumnsTable
        columns={props.columns}
        avaiableMergeFieldTopics={props.availableMergeFieldTopics}
        onDelete={onDelete}
        onSave={onSave}
        onOrderChange={onOrderChange}
      />
    </SectionPanel.Panel>
    <AddColumnsModal
      columns={props.columns}
      availableMergeFieldTopics={props.availableMergeFieldTopics}
      isOpen={isAddColumnsModalOpen}
      onClose={onAddModalClose}
    />
  </>
}

export const ColumnsPanelForNew = (props: Props) => {
  const [isAddColumnsModalOpen, setIsAddColumnsModalOpen] = React.useState(false);

  const onDelete = async (column: server.dto.ExcelTemplateColumn) => {
    const filtered = props.columns.filter(c => c.mergeField !== column.mergeField);
    props.onColumnsUpdated(filtered);
  }

  const onSave = async (column: server.dto.ExcelTemplateColumn) => {
    const updated = props.columns.map(c => c.mergeField === column.mergeField ? column : c);
    props.onColumnsUpdated(updated);
  }

  const onOrderChange = async (newColumns: server.dto.ExcelTemplateColumn[]) => {
    props.onColumnsUpdated(newColumns);
  }

  const onAddModalClose = async (mergeFields: string[]) => {
    setIsAddColumnsModalOpen(false);

    const { columnsToAdd, columnsToRemove } = getColumnsToAddAndColumnsToRemove(props.columns, mergeFields);
    const updatedColumns = props.columns.filter(c => !columnsToRemove.some(r => r.mergeField === c.mergeField)).concat(columnsToAdd);

    props.onColumnsUpdated(updatedColumns);
  }

  return <>
    <SectionPanel.Panel
      header={<>
        <PanelSegment.Title title={RESX.Template.Columns} />
        <PanelSegment.Button onClick={() => {
          setIsAddColumnsModalOpen(true);
        }} label={RESX.Buttons.Add} type="secondary" />
      </>}
    >
      <ColumnsTable
        columns={props.columns}
        avaiableMergeFieldTopics={props.availableMergeFieldTopics}
        onDelete={onDelete}
        onSave={onSave}
        onOrderChange={onOrderChange}
      />
    </SectionPanel.Panel>
    <AddColumnsModal
      columns={props.columns}
      availableMergeFieldTopics={props.availableMergeFieldTopics}
      isOpen={isAddColumnsModalOpen}
      onClose={onAddModalClose}
    />
  </>
}

const getColumnsToAddAndColumnsToRemove = (currentColumns: server.dto.ExcelTemplateColumn[], newMergeFields: string[]) => {
  const currentMergeFieldSet = new Set(currentColumns.map(c => c.mergeField));
  const newMergeFieldSet = new Set(newMergeFields);

  const mergeFieldsToAdd = new Set(newMergeFields.filter(mf => !currentMergeFieldSet.has(mf)));
  const mergeFieldsToRemove = new Set(currentColumns.filter(c => !newMergeFieldSet.has(c.mergeField)).map(c => c.mergeField));

  const columnsToAdd = Array.from(mergeFieldsToAdd).map(mf => ({
    mergeField: mf,
    headerLabel: "",
    hexColor: serverReference.constants.excelTemplate.defaultColumnColor,
  } as server.dto.ExcelTemplateColumn));

  const columnsToRemove = currentColumns.filter(c => mergeFieldsToRemove.has(c.mergeField));

  return { columnsToAdd, columnsToRemove };
}